/**
 * App shell navigation — path-based URLs for main screens (Option A).
 *
 * Public paths:
 *   /plan    → Plan
 *   /recipes → Recipes (internal tab id: recipes)
 *   /shop    → Shop
 *   /pantry  → Pantry
 *   /account → Account
 *
 * Guest kitchen setup paths:
 *   /setup/pantry
 *   /setup/recipes
 *   /setup/preferences
 *   /setup/plan
 *
 * Recipe detail and Cook Mode stay on query params:
 *   /recipes?tab=recipe&id=:id
 *   /recipes?tab=cook&id=:id&session=:sessionId
 *
 * Legacy `/?tab=` URLs and `/cook` are redirected to canonical paths on first read.
 */

import {
  buildCookModeResumeHref,
  peekCookModeResume,
} from './cookModeResume.js'

/** @typedef {'plan'|'recipes'|'shop'|'pantry'|'account'|'recipe'|'cook'} AppTab */

/** @type {Record<string, AppTab>} */
export const PATH_TO_TAB = {
  '/plan': 'plan',
  '/recipes': 'recipes',
  '/cook': 'recipes',
  '/shop': 'shop',
  '/pantry': 'pantry',
  '/account': 'account',
  '/setup/pantry': 'pantry',
  '/setup/recipes': 'recipes',
  '/setup/preferences': 'plan',
  '/setup/plan': 'plan',
}

export const SETUP_STEP_TO_PATH = {
  1: '/setup/pantry',
  2: '/setup/recipes',
  3: '/setup/preferences',
  4: '/setup/plan',
}

const SETUP_PATH_TO_STEP = Object.fromEntries(
  Object.entries(SETUP_STEP_TO_PATH).map(([step, path]) => [path, Number(step)]),
)

/** @type {Record<AppTab, string>} */
export const TAB_TO_PATH = {
  plan: '/plan',
  recipes: '/recipes',
  shop: '/shop',
  pantry: '/pantry',
  account: '/account',
}

const MAIN_TABS = new Set(['plan', 'recipes', 'shop', 'pantry', 'account'])

/** Normalize saved tab: migrate old names and enforce valid main tabs. Default is Plan. */
export function normalizeSavedTab(saved) {
  if (saved === 'admin' || saved === 'documents') return 'shop'
  if (saved === 'grocery') return 'shop'
  if (saved === 'chat') return 'recipes'
  if (saved === 'profile' || saved === 'preferences') return 'account'
  if (saved === 'item-admin' || saved === 'system-admin') return 'account'
  if (saved === 'shop' || saved === 'recipes' || saved === 'pantry' || saved === 'account' || saved === 'plan') {
    return saved
  }
  if (saved === 'recipe' || saved === 'cook') return saved
  return 'plan'
}

export function normalizePathname(pathname) {
  if (!pathname || pathname === '/') return '/'
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed || '/'
}

/** Read recipe/cook params from the URL search string. */
export function readRecipeParams(search) {
  try {
    const p = new URLSearchParams(search)
    return {
      id: p.get('id') ?? null,
      session: p.get('session') ?? null,
      planItemId: p.get('planItemId') ?? null,
    }
  } catch {
    return { id: null, session: null, planItemId: null }
  }
}

/**
 * Resolve the active shell tab from pathname + search.
 * @param {Pick<Location, 'pathname'|'search'>} [location]
 * @returns {AppTab|null}
 */
export function readTabFromLocation(location = typeof window !== 'undefined' ? window.location : { pathname: '/', search: '' }) {
  try {
    const pathname = normalizePathname(location.pathname)
    const params = new URLSearchParams(location.search || '')
    const queryTab = params.get('tab')

    if (queryTab === 'recipe' || queryTab === 'cook') {
      return queryTab
    }

    if (PATH_TO_TAB[pathname]) {
      return PATH_TO_TAB[pathname]
    }

    if (pathname === '/' && queryTab) {
      return normalizeSavedTab(queryTab)
    }

    return null
  } catch {
    return null
  }
}

/** Read the guest kitchen setup step encoded by the current path. */
export function readSetupStepFromLocation(
  location = typeof window !== 'undefined' ? window.location : { pathname: '/' },
) {
  try {
    return SETUP_PATH_TO_STEP[normalizePathname(location.pathname)] ?? null
  } catch {
    return null
  }
}

/**
 * True when the URL encodes an explicit destination other than the default Plan home.
 * Used so post-login navigation does not override deep links.
 */
export function hasNavigationDeepLink(location = typeof window !== 'undefined' ? window.location : { pathname: '/', search: '' }) {
  const pathname = normalizePathname(location.pathname)
  const params = new URLSearchParams(location.search || '')
  const queryTab = params.get('tab')

  if (queryTab === 'recipe' || queryTab === 'cook') return true

  if (pathname === '/') {
    if (!queryTab) return false
    return normalizeSavedTab(queryTab) !== 'plan'
  }

  if (pathname === '/plan') return false

  return Boolean(PATH_TO_TAB[pathname])
}

/**
 * Redirect legacy `/?tab=` URLs to canonical paths (replaceState, no extra history entry).
 * @returns {boolean} whether a redirect was applied
 */
/**
 * Redirect the old library address `/cook` to `/recipes`, keeping the query
 * so a recipe and a Cook Mode session still open.
 * @returns {boolean} whether a redirect was applied
 */
export function applyRecipesPathRedirect(location = typeof window !== 'undefined' ? window.location : null) {
  if (typeof window === 'undefined' || !location) return false

  const url = new URL(location.href)
  if (normalizePathname(url.pathname) !== '/cook') return false

  url.pathname = TAB_TO_PATH.recipes
  const search = url.searchParams.toString()
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
  window.history.replaceState({}, document.title, next)
  return true
}

export function applyLegacyUrlRedirect(location = typeof window !== 'undefined' ? window.location : null) {
  if (typeof window === 'undefined' || !location) return false

  const url = new URL(location.href)
  const pathname = normalizePathname(url.pathname)
  const queryTab = url.searchParams.get('tab')

  if (pathname !== '/' || !queryTab) return false

  const normalized = normalizeSavedTab(queryTab)
  let targetPath = null

  if (normalized === 'recipe' || normalized === 'cook') {
    targetPath = TAB_TO_PATH.recipes
  } else if (MAIN_TABS.has(normalized)) {
    targetPath = TAB_TO_PATH[normalized]
  }

  if (!targetPath) return false

  url.pathname = targetPath
  if (normalized !== 'recipe' && normalized !== 'cook') {
    url.searchParams.delete('tab')
  }

  const search = url.searchParams.toString()
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
  window.history.replaceState({}, document.title, next)
  return true
}

export function isAuthRequiredTab(tab) {
  if (tab === 'recipe' || tab === 'cook') return false
  return Boolean(tab)
}

export function buildRecipeDetailUrl(recipeId) {
  return `${TAB_TO_PATH.recipes}?tab=recipe&id=${encodeURIComponent(recipeId)}`
}

export function buildCookModeUrl({ recipeId, sessionId = null, planItemId = null } = {}) {
  const params = new URLSearchParams({ tab: 'cook', id: recipeId })
  if (sessionId) params.set('session', sessionId)
  if (planItemId) params.set('planItemId', planItemId)
  return `${TAB_TO_PATH.recipes}?${params.toString()}`
}

/** Plan tab with optional pick/recipe highlight and week (Cook → Plan handoff). */
export function buildPlanUrl({ highlight = null, weekStart = null } = {}) {
  const params = new URLSearchParams()
  if (highlight) params.set('highlight', String(highlight))
  if (weekStart) params.set('week', String(weekStart))
  const qs = params.toString()
  return qs ? `/plan?${qs}` : '/plan'
}

/** Read `?highlight=` from Plan URL (recipe id to focus in Your picks / week). */
export function readPlanHighlight(search = typeof window !== 'undefined' ? window.location.search : '') {
  try {
    const id = new URLSearchParams(search).get('highlight')
    return id && String(id).trim() ? String(id).trim() : null
  } catch {
    return null
  }
}

/** Read `?week=` (Monday YYYY-MM-DD) from Plan URL. */
export function readPlanWeekStart(search = typeof window !== 'undefined' ? window.location.search : '') {
  try {
    const week = new URLSearchParams(search).get('week')
    return week && /^\d{4}-\d{2}-\d{2}$/.test(week) ? week : null
  } catch {
    return null
  }
}

/** Event: navigate to Plan, optionally highlighting a recipe pick. */
export const APP_NAVIGATE_PLAN_EVENT = 'app:navigate-plan'

/** Dispatch shell navigation to Plan (optional highlight + week). App.jsx listens. */
export function navigateToPlan({ highlight = null, weekStart = null } = {}) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(APP_NAVIGATE_PLAN_EVENT, {
      detail: {
        highlight: highlight != null ? String(highlight) : null,
        weekStart: weekStart != null ? String(weekStart) : null,
      },
    }),
  )
}

function commitUrl(url, { replace = false, state = {} } = {}) {
  const search = url.searchParams.toString()
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
  if (replace) window.history.replaceState(state, document.title, next)
  else window.history.pushState(state, document.title, next)
}

/** Write Plan URL with optional recipe highlight and week start. */
export function writePlanUrl({ highlight = null, weekStart = null } = {}, { replace = false } = {}) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.pathname = TAB_TO_PATH.plan
  url.searchParams.delete('tab')
  url.searchParams.delete('id')
  url.searchParams.delete('session')
  url.searchParams.delete('planItemId')
  if (highlight) url.searchParams.set('highlight', String(highlight))
  else url.searchParams.delete('highlight')
  if (weekStart) url.searchParams.set('week', String(weekStart))
  else url.searchParams.delete('week')
  commitUrl(url, { replace })
}

/** Drop `?highlight=` without leaving Plan (after highlight animation). */
export function clearPlanHighlightFromUrl() {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has('highlight')) return
  url.searchParams.delete('highlight')
  commitUrl(url, { replace: true })
}

/** Write a main-screen tab to the URL (path-based). */
export function writeTabToUrl(tab, { replace = false } = {}) {
  if (typeof window === 'undefined') return
  const path = TAB_TO_PATH[tab]
  if (!path) return

  const url = new URL(window.location.href)
  url.pathname = path
  url.searchParams.delete('tab')
  url.searchParams.delete('id')
  url.searchParams.delete('session')
  url.searchParams.delete('planItemId')
  url.searchParams.delete('highlight')
  url.searchParams.delete('week')
  commitUrl(url, { replace })
}

/** App surface that renders a guest kitchen setup step. */
export function tabForSetupStep(step) {
  if (Number(step) === 1) return 'pantry'
  if (Number(step) === 2) return 'recipes'
  return 'plan'
}

/** Step recorded on a history entry written by setup navigation. */
export function readSetupHistoryStep(state) {
  const step = Number(state?.captainCookSetupStep)
  return Number.isInteger(step) && step >= 1 && step <= 4 ? step : null
}

/**
 * How an in-app setup move should touch history.
 * A single backward move uses the browser entry when setup pushed it.
 * Direct visits and longer jumps replace, so Back cannot leave the site.
 */
export function setupHistoryMove(currentStep, nextStep, historyState) {
  const step = Math.min(4, Math.max(1, Number(nextStep) || 1))
  const current = Math.min(4, Math.max(1, Number(currentStep) || 1))
  if (step === current) return 'none'
  if (step === current - 1 && readSetupHistoryStep(historyState) === current) return 'back'
  if (step < current) return 'replace'
  return 'push'
}

/**
 * Browser Back/Forward while a guest kitchen exists.
 * Completed setup never reopens. Leaving the setup paths returns home.
 */
export function guestSetupPopAction({ setupStep = null, onboardingComplete = false, onboardingActive = false } = {}) {
  if (setupStep && onboardingComplete) return 'plan'
  if (setupStep && onboardingActive) return 'sync-step'
  if (!setupStep && onboardingActive) return 'home'
  return 'tab'
}

/** Write one guest kitchen setup step to its dedicated path. */
export function writeSetupStepToUrl(step, { replace = false } = {}) {
  if (typeof window === 'undefined') return
  const path = SETUP_STEP_TO_PATH[Number(step)]
  if (!path) return

  const url = new URL(window.location.href)
  url.pathname = path
  url.searchParams.delete('tab')
  url.searchParams.delete('id')
  url.searchParams.delete('session')
  url.searchParams.delete('planItemId')
  url.searchParams.delete('highlight')
  url.searchParams.delete('week')
  commitUrl(url, {
    replace,
    state: { captainCookSetupStep: Number(step) },
  })
}

/** Write recipe detail or cook mode to the URL (overlay query params on /recipes). */
export function writeRecipeUrl({ tab, id, session, planItemId }, { replace = false } = {}) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.pathname = TAB_TO_PATH.recipes
  url.searchParams.set('tab', tab)
  if (id) url.searchParams.set('id', id)
  else url.searchParams.delete('id')
  if (session) url.searchParams.set('session', session)
  else url.searchParams.delete('session')
  if (planItemId) url.searchParams.set('planItemId', planItemId)
  else url.searchParams.delete('planItemId')
  url.searchParams.delete('highlight')
  url.searchParams.delete('week')
  commitUrl(url, { replace })
}

/**
 * Reset URL to `/` without navigation params so post-logout sign-in can default to Plan.
 * (goToPlanHome skips when hasNavigationDeepLink() is true.)
 */
export function clearNavigationFromUrl() {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const hadNav =
    url.pathname !== '/' ||
    url.searchParams.has('tab') ||
    url.searchParams.has('id') ||
    url.searchParams.has('session') ||
    url.searchParams.has('planItemId') ||
    url.searchParams.has('highlight') ||
    url.searchParams.has('week')
  if (!hadNav) return

  url.pathname = '/'
  url.searchParams.delete('tab')
  url.searchParams.delete('id')
  url.searchParams.delete('session')
  url.searchParams.delete('planItemId')
  url.searchParams.delete('highlight')
  url.searchParams.delete('week')
  const search = url.searchParams.toString()
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
  window.history.replaceState({}, document.title, next)
}

/**
 * Initial tab from URL so first paint shows the correct screen.
 * Default home is Plan (`/` with no destination → Plan).
 * Last-tab localStorage is not restored here: path-based URLs already preserve
 * intentional destinations on refresh; restoring storage made cold starts at `/`
 * land on Cook after visiting that tab.
 */
export function getInitialTabSelection() {
  try {
    applyLegacyUrlRedirect()
    applyRecipesPathRedirect()
    const fromUrl = readTabFromLocation(window.location)
    if (fromUrl === 'cook' || fromUrl === 'recipe') {
      const { id } = readRecipeParams(window.location.search)
      // Overlay without an id cannot load — fall through to resume / Plan home.
      if (id) return { tab: fromUrl, source: 'url' }
    } else if (fromUrl) {
      return { tab: fromUrl, source: 'url' }
    }

    // PWA cold starts often open start_url "/plan" (or "/") and drop the cook deep link.
    // Restore an in-progress Cook Mode session when we have one saved.
    const resume = peekCookModeResume()
    const resumeHref = resume ? buildCookModeResumeHref(resume) : null
    if (resumeHref) {
      window.history.replaceState({}, document.title, resumeHref)
      return { tab: 'cook', source: 'cook_resume' }
    }

    return { tab: 'plan', source: 'default' }
  } catch {
    return { tab: 'plan', source: 'default' }
  }
}
